import { fetchScreenshots } from '../api';

interface PreloadCacheEntry {
  data: any;
  timestamp: number;
  promise?: Promise<any>;
}

class PreloadService {
  private cache: Map<string, PreloadCacheEntry> = new Map();
  private readonly CACHE_TTL = 10 * 60 * 1000; // 10 minutes
  private readonly MAX_CACHE_SIZE = 5; // Maximum number of preloaded runs

  private generateKey(runId: string): string {
    return `screenshots-${runId}`;
  }

  // Get data from cache if available
  get(runId: string): any | null {
    const key = this.generateKey(runId);
    const entry = this.cache.get(key);
    
    if (!entry) return null;
    
    // Check if data is still fresh
    if (Date.now() - entry.timestamp > this.CACHE_TTL) {
      this.cache.delete(key);
      return null;
    }
    
    // If we have the data, return it
    if (entry.data) {
      console.log(`[PreloadService] Cache hit for run ${runId}`);
      return entry.data;
    }
    
    // If we're still loading, return null (caller should use normal loading)
    return null;
  }

  // Get the promise if data is being preloaded
  getPromise(runId: string): Promise<any> | null {
    const key = this.generateKey(runId);
    const entry = this.cache.get(key);
    
    if (!entry || !entry.promise) return null;
    
    return entry.promise;
  }

  // Check if we're currently preloading a run
  isPreloading(runId: string): boolean {
    const key = this.generateKey(runId);
    const entry = this.cache.get(key);
    return !!(entry && entry.promise && !entry.data);
  }

  // Preload screenshots for a run in the background
  async preload(problemName: string, runId: string): Promise<void> {
    const key = this.generateKey(runId);
    
    // Don't preload if already in cache or currently loading
    if (this.cache.has(key)) {
      console.log(`[PreloadService] Already cached or loading: ${runId}`);
      return;
    }
    
    console.log(`[PreloadService] Starting preload for run ${runId}`);
    
    // Clean up old entries if cache is getting full
    this.cleanupCache();
    
    // Create a promise for the loading operation
    const loadPromise = fetchScreenshots(problemName, runId)
      .then(data => {
        console.log(`[PreloadService] Successfully preloaded run ${runId}`);
        const entry = this.cache.get(key);
        if (entry) {
          entry.data = data;
          entry.promise = undefined;
        }
        return data;
      })
      .catch(error => {
        console.error(`[PreloadService] Failed to preload run ${runId}:`, error);
        // Remove failed entry from cache
        this.cache.delete(key);
        throw error;
      });
    
    // Store the promise in cache while loading
    this.cache.set(key, {
      data: null,
      timestamp: Date.now(),
      promise: loadPromise
    });
  }

  // Clean up old cache entries
  private cleanupCache(): void {
    if (this.cache.size <= this.MAX_CACHE_SIZE) return;
    
    // Sort entries by timestamp and remove oldest
    const entries = Array.from(this.cache.entries())
      .filter(([_, entry]) => entry.data !== null) // Only consider loaded entries
      .sort((a, b) => a[1].timestamp - b[1].timestamp);
    
    // Remove oldest entries until we're under the limit
    const toRemove = entries.slice(0, entries.length - this.MAX_CACHE_SIZE + 1);
    toRemove.forEach(([key]) => {
      console.log(`[PreloadService] Removing old cache entry: ${key}`);
      this.cache.delete(key);
    });
  }

  // Clear specific entry or entire cache
  clear(runId?: string): void {
    if (runId) {
      const key = this.generateKey(runId);
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }

  // Get cache statistics for debugging
  getStats(): { size: number; entries: string[] } {
    return {
      size: this.cache.size,
      entries: Array.from(this.cache.keys())
    };
  }
}

// Export singleton instance
export const preloadService = new PreloadService();